/**
 * Fix for dwv's broken compressed-transfer-syntax decode-worker path (see
 * DicomViewer.tsx's own module docstring for the user-facing symptom: a
 * decode that hangs forever with no "load"/"error" event, for exactly the
 * transfer syntaxes that need one of dwv's own bundled decode workers).
 *
 * ROOT CAUSE, confirmed by reading dwv 0.36.4's actual shipped source
 * (`node_modules/dwv/dist/dwv.min.js`), not just its (incomplete — no
 * "worker"/"decoderScripts" entry at all) `dwv.d.ts`: dwv is built with
 * webpack 5, which compiles each decode worker's construction to
 *
 *   new Worker(new URL("./" + i.u(<id>), i.b), {name: "...worker", type: "module"})
 *
 * where `i.u(<id>)` returns a fixed relative filename such as
 * `"assets/workers/jpegloss.worker.min.js"`, and `i.b` — webpack's
 * `workerPublicPath` runtime value — is set, ONCE, at module-evaluation
 * time, by this exact line also present verbatim in the shipped bundle:
 *
 *   i.b = "undefined" != typeof document && document.baseURI || self.location.href
 *
 * dwv's build never configures webpack's `output.workerPublicPath`, so it
 * falls back to `document.baseURI` (the CURRENT PAGE's own URL, not the site
 * root). This is a private, closure-scoped variable inside dwv's bundled ES
 * module — there is no exported binding, no `dwv.App`/`dwv.AppOptions`
 * field, and no documented global for it (dwv's own official guidance for
 * bundler consumers — see the "DWV's Web workers" section of the
 * ivmartel/dwv-vue reference app's README — is simply "copy the worker files
 * into your public/assets folder"; it says nothing about `document.baseURI`
 * at all, because dwv's own reference apps are only ever exercised from the
 * site root). Every route this app's DicomViewer can be reached from is
 * nested at least one path segment deep (see router.tsx:
 * "diagnostics/imaging/:studyId" under a portal prefix) — so
 * `document.baseURI` here is never the site root, and dwv's relative
 * "./assets/workers/*.worker.min.js" resolves to entirely the wrong path
 * every single time, 404-ing the worker silently (a failed dynamic
 * module-worker script load fires neither "load" nor "error" on the dwv.App
 * — see DECODE_TIMEOUT_MS in DicomViewer.tsx).
 *
 * Confirmed EMPIRICALLY (not just by reading the source) against the real,
 * unmocked package: feeding a real dwv.App a synthetic classic-JPEG-lossless
 * DICOM file from a simulated nested route
 * ("http://localhost/patient/diagnostics/imaging/<id>") made dwv construct
 * `new Worker("http://localhost/patient/diagnostics/imaging/assets/workers/jpegloss.worker.min.js")`
 * — a path that 404s, since this repo actually serves the copied worker
 * files at the site ROOT, "/assets/workers/...". See dwvWorkerSetup.test.ts.
 *
 * FIX: since dwv gives applications no supported override for this, and a
 * page-wide `<base href>` change would silently alter every OTHER relative
 * URL on the page (every link, relative fetch, image, form action — far too
 * invasive for one library's bug), this instead patches the global `Worker`
 * constructor itself, narrowly: only a URL matching dwv's own fixed
 * "assets/workers/<name>.worker.min.js" naming scheme (the exact 6 files
 * dwv ships and this app already copies into public/assets/workers/, see
 * that folder) is rewritten, to an absolute, root-relative URL. Every other
 * use of `new Worker(...)` anywhere else in the app (there is currently
 * none — grepped) is passed through completely untouched.
 *
 * Must run once, before the first `dwv.App` is ever constructed (module load
 * of DicomViewer.tsx is early enough — dwv only actually constructs a Worker
 * lazily, the first time a compressed-transfer-syntax decode is dispatched,
 * long after this module has already run).
 */

const DWV_WORKER_PATH_RE = /assets\/workers\/[^/?#]+\.worker\.min\.js(?:[?#].*)?$/;

let patched = false;

/** Test-only seam: lets the test suite reset the module-level guard between
 *  cases that need to install a fresh spy on `window.Worker` (the guard
 *  otherwise means only the FIRST call in a whole test run actually patches
 *  anything, since a real app only ever calls this once). Never called from
 *  application code. */
export function __resetDwvWorkerPatchForTesting(): void {
  patched = false;
}

/**
 * Patches the global `Worker` constructor so that any URL matching dwv's own
 * `assets/workers/<name>.worker.min.js` naming scheme resolves to this app's
 * actual root-relative `/assets/workers/...` path, regardless of what base
 * URL dwv itself tried to resolve it against. Safe to call more than once
 * (a no-op after the first call) and safe to call where `window`/`Worker`
 * don't exist (SSR, Node test environments without a Worker global) — it
 * simply does nothing in that case, same as before this module existed.
 */
export function ensureDwvWorkerPathsResolveCorrectly(): void {
  if (patched) return;
  if (typeof window === "undefined" || typeof window.Worker === "undefined") return;
  patched = true;

  const OriginalWorker = window.Worker;

  class PatchedWorker extends OriginalWorker {
    constructor(scriptURL: string | URL, options?: WorkerOptions) {
      const href = typeof scriptURL === "string" ? scriptURL : scriptURL.href;
      const match = href.match(DWV_WORKER_PATH_RE);
      const fixedURL = match ? new URL(`/${match[0]}`, window.location.origin) : scriptURL;
      super(fixedURL, options);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.Worker = PatchedWorker as any;
}
